import React, { useState } from "react";
import { DEFAULT_RESEARCH_TYPE } from "../../config/constants.ts";
import { Button } from "../../ui-library/Button/Button.tsx";
import { Card, CardSubtitle, CardTitle } from "../../ui-library/Card/Card.tsx";
import { Dropdown } from "../../ui-library/Dropdown/Dropdown.tsx";
import { InputLabel } from "../../ui-library/InputLabel/InputLabel.tsx";
import { LoadingDots } from "../../ui-library/LoadingDots/LoadingDots.tsx";
import { TextArea } from "../../ui-library/TextArea/TextArea.tsx";
import { Tooltip } from "../../ui-library/Tooltip/Tooltip.tsx";
import { FileUploadTermsModal } from "../TermsAndConditions/FileUploadTermsModal.tsx";
import styles from "./WorkflowWizard.module.css";
import {
  KnowledgeSource,
  KnowledgeSourceDropdown,
} from "../KnowledgeSourceDropdown/KnowledgeSourceDropdown.tsx";

type FeatureFlags = {
  enableResearchTypes?: boolean;
  enableResearchTypeDescriptions?: boolean;
  enableIngestionFolderLink?: boolean;
  enableTermsAndConditions?: boolean;
};
export const WorkflowWizard: React.FC<{
  onProjectStart: (
    source: string,
    researchGoal: string,
    specificQuestions: string,
    researchType?: string,
  ) => void;
  knowledgeSources: KnowledgeSource[];
  loading?: boolean;
  knowledgeSourcesLoading?: boolean;
  researchTypes: {
    items: { name: string; display_name: string; description: string }[];
    loading: boolean;
  };
  featureFlags: FeatureFlags;
}> = (props) => {
  const [selectedSource, setSelectedSource] = useState<
    KnowledgeSource | undefined
  >();
  const [researchGoal, setResearchGoal] = useState<string>("");
  const [specificQuestions, setSpecificQuestions] = useState<string>("");
  const [selectedResearchType, setSelectedResearchType] = useState<string>(
    DEFAULT_RESEARCH_TYPE,
  );
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const isFormValid = !!(selectedSource && researchGoal);

  const handleAddFilesClick = (event: React.MouseEvent) => {
    event.preventDefault();
    if (props.featureFlags.enableTermsAndConditions) {
      setShowTermsModal(true);
    } else if (selectedSource?.repoUrl) {
      window.open(selectedSource.repoUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleTermsAccept = () => {
    setShowTermsModal(false);
    if (selectedSource?.repoUrl) {
      window.open(selectedSource.repoUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleTermsCancel = () => {
    setShowTermsModal(false);
  };
  return (
    <div className={styles.WizardContainer}>
      <Card className={styles.Card}>
        <CardSubtitle>Workflow Builder</CardSubtitle>
        <CardTitle>Curation Project</CardTitle>
        <div className={styles.InputContainer}>
          <KnowledgeSourceDropdown
            knowledgeSourcesLoading={props.knowledgeSourcesLoading}
            knowledgeSources={props.knowledgeSources}
            selectedSource={selectedSource}
            onSelect={(value) => {
              const selected = props.knowledgeSources.find(
                (source) => source.value === value[0],
              );
              setSelectedSource(selected);
            }}
            featureFlags={props.featureFlags}
            onClick={handleAddFilesClick}
          />
        </div>
        <div className={styles.InputContainer}>
          <InputLabel number={2} for={"research-goal"} size={"large"}>
            Define Research Goal *
          </InputLabel>
          <TextArea
            onChange={setResearchGoal}
            value={researchGoal}
            name={"research-goal"}
          />
        </div>
        <div className={styles.InputContainer}>
          <InputLabel number={3} for={"specific-questions"} size={"large"}>
            Specific questions
            <Tooltip text="These specific questions are designed to generate better answers based on your curation goal and will not be addressed individually. If you prefer individual responses, please use the interrogation step after the workflow is processed." />
          </InputLabel>
          <TextArea
            onChange={setSpecificQuestions}
            value={specificQuestions}
            name={"specific-questions"}
          />
        </div>
        {props.featureFlags.enableResearchTypes && (
          <div className={styles.InputContainer}>
            <InputLabel number={4} size={"large"}>
              Select Methodology
            </InputLabel>
            {props.researchTypes.loading ? (
              <LoadingDots />
            ) : (
              <Dropdown
                items={props.researchTypes.items.map((type) => ({
                  label: type.display_name,
                  value: type.name,
                  details: props.featureFlags.enableResearchTypeDescriptions
                    ? type.description
                    : undefined,
                }))}
                placeholder="Select methodology"
                selected={selectedResearchType ? [selectedResearchType] : []}
                onSelect={(value) => setSelectedResearchType(value[0])}
              />
            )}
          </div>
        )}
        <div className={styles.ButtonContainer}>
          <Button
            onClick={() => {
              if (!isFormValid) return;
              props.onProjectStart(
                selectedSource?.value,
                researchGoal,
                specificQuestions,
                selectedResearchType,
              );
            }}
            disabled={!isFormValid || props.loading}
          >
            Start{props.loading && "ing..."}
          </Button>
        </div>
      </Card>
      <FileUploadTermsModal
        open={showTermsModal}
        onAccept={handleTermsAccept}
        onCancel={handleTermsCancel}
      />
    </div>
  );
};
